<template>
  <div
    ref="root"
    class="hanzi-editor"
  >
    <div class="editor-wrapper">
      <EditorContent :editor="editor" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import History from '@tiptap/extension-history'
import { Extension } from '@tiptap/core'
import { Node, mergeAttributes } from '@tiptap/core'
import { pinyin, convert } from 'pinyin-pro'
import { type StringOrHanzi } from '@/types/model'
import Placeholder from '@tiptap/extension-placeholder'
import tippy from 'tippy.js'
import PinyinSelection from './PinyinSelection.vue'
import { createApp, ref } from 'vue'
import { closest } from '@/utils/misc'

const content = defineModel<StringOrHanzi[]>()
const root = ref(null)

const props = defineProps<{
  placeholder?: string
}>()

const emit = defineEmits<{
  focus: []
  blur: []
}>()

const HanziNode = Node.create({
  name: 'hanzi',
  group: 'inline',
  inline: true,
  selectable: false,

  addAttributes() {
    return {
      char: {
        default: '',
      },
      pinyin: {
        default: '',
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-hanzi]',
        getAttrs: dom => ({
          char: (dom as HTMLElement).getAttribute('data-hanzi-char'),
          pinyin: (dom as HTMLElement).getAttribute('data-hanzi-pinyin'),
        }),
      },
    ]
  },

  renderHTML({ node }) {
    return [
      'span',
      mergeAttributes({
        'data-hanzi': 'true',
        'data-hanzi-char': node.attrs.char,
        'data-hanzi-pinyin': node.attrs.pinyin,
      }),
      ['ruby', {}, node.attrs.char, ['rt', {}, convert(node.attrs.pinyin)]],
    ]
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement('span')
      dom.setAttribute('data-hanzi', 'true')
      dom.setAttribute('data-hanzi-char', node.attrs.char)
      dom.setAttribute('data-hanzi-pinyin', node.attrs.pinyin)

      const ruby = document.createElement('ruby')
      ruby.textContent = node.attrs.char

      const rt = document.createElement('rt')
      rt.textContent = convert(node.attrs.pinyin)

      ruby.appendChild(rt)
      dom.appendChild(ruby)

      const tooltip = tippy(dom, {
        onShow(instance: any) {
          const tooltipContent = document.createElement('div')
          const app = createApp(PinyinSelection, {
            hanzi: dom.getAttribute('data-hanzi-char'),
            curPinyin: dom.getAttribute('data-hanzi-pinyin'),
            onSelect(newPinyin: string) {
              const char = dom.getAttribute('data-hanzi-char')

              if (typeof getPos === 'function') {
                editor
                  .chain()
                  .focus()
                  .command(({ tr }) => {
                    const pos = getPos()
                    tr.setNodeMarkup(pos, undefined, {
                      char: char,
                      pinyin: newPinyin,
                    })
                    return true
                  })
                  .run()
              }

              tooltip.hide()
            },
          })
          app.mount(tooltipContent)
          instance.setContent(tooltipContent)
        },
        content: '',
        arrow: false,
        trigger: 'click',
        placement: 'bottom',
        interactive: true,
        appendTo: root.value,
        // appendTo: closest(root.value, '.card-pad'),
      })

      dom.addEventListener('mousedown', event => {
        // 阻止光标移动
        event.preventDefault()
      })

      return {
        dom,
        update: updatedNode => {
          if (
            updatedNode.attrs.char !== node.attrs.char ||
            updatedNode.attrs.pinyin !== node.attrs.pinyin
          ) {
            dom.setAttribute('data-hanzi-char', updatedNode.attrs.char)
            dom.setAttribute('data-hanzi-pinyin', updatedNode.attrs.pinyin)
            ruby.firstChild!.textContent = updatedNode.attrs.char
            rt.textContent = convert(updatedNode.attrs.pinyin)
            return true
          }
          return false
        },
      }
    }
  },
})

const ProcessHanzi = Extension.create({
  name: 'processHanzi',
  onCreate() {
    // Sets up event listener for textInput
    this.editor.on('update', ({ editor, transaction }) => {
      // Skip if there's no transaction or if we're the source of this transaction
      if (!transaction || transaction.getMeta('processHanziUpdate')) {
        return
      }

      // Start our own transaction
      const { state } = editor
      const tr = state.tr.setMeta('processHanziUpdate', true)
      let modified = false

      // Function to check if character is Chinese
      const isChineseCharacter = char => {
        return /[\u4e00-\u9fa5]/.test(char)
      }

      // Process all text nodes
      state.doc.descendants((node, pos) => {
        if (node.isText) {
          let lastPos = pos
          const text = node.text

          // Check each character
          for (let i = 0; i < text.length; i++) {
            const char = text.charAt(i)

            if (isChineseCharacter(char)) {
              const pinyinText = pinyin(char, { toneType: 'num' })

              // Create hanzi node
              const hanziNode = state.schema.nodes.hanzi.create({
                char: char,
                pinyin: pinyinText,
              })

              // Replace text with hanzi node
              tr.replaceWith(lastPos + i, lastPos + i + 1, hanziNode)
              modified = true
            }
          }
        }
      })

      // Only dispatch if we made changes
      if (modified) {
        this.editor.view.dispatch(tr)
      }
    })
  },
})

const CustomKeymap = Extension.create({
  name: 'customKeymap',

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        editor.commands.insertContent({ type: 'hardBreak' })
        return true
      },
    }
  },
})

function jsonToModel(json: any): StringOrHanzi[] {
  const result: StringOrHanzi[] = []

  if (json.content[0].content) {
    for (const node of json.content[0].content) {
      switch (node.type) {
        case 'hanzi':
          {
            result.push(['hanzi', node.attrs.char, node.attrs.pinyin])
          }
          break
        case 'text':
          {
            result.push(node.text)
          }
          break
        default: {
          throw new Error(`[HanziEditor] invalid node type: ${node.type}`)
        }
      }
    }
  }

  return result
}

function modelToJson(model: StringOrHanzi[] | undefined): object {
  const content: any[] = []
  const result = { type: 'doc', content: [{ type: 'p', content }] } as any

  if (model) {
    for (const item of model) {
      if (typeof item === 'string') {
        content.push({ type: 'text', text: item })
      } else if (Array.isArray(item) && item[0] === 'hanzi') {
        content.push({
          type: 'hanzi',
          attrs: { char: item[1], pinyin: item[2] },
        })
      } else {
        throw new Error(
          `[HanziEditor] invalid model item: ${JSON.stringify(item)}`
        )
      }
    }
  }

  return result
}

const editor = useEditor({
  extensions: [
    Node.create({
      name: 'doc',
      topNode: true,
      content: 'p',
    }),
    Node.create({
      name: 'p',
      content: 'inline*',
      parseHTML() {
        return [{ tag: 'p' }]
      },
      renderHTML() {
        return ['p', 0]
      },
    }),
    // 这里也要使用 span 来渲染，这样和 hanzi 对齐
    Node.create({
      name: 'text',
      group: 'inline',
    }),
    HanziNode,
    // Node.create({
    //   name: 'hardBreak',
    //   inline: true,
    //   group: 'inline',
    //   selectable: false,
    //   parseHTML() {
    //     return [{ tag: 'br' }]
    //   },
    //   renderHTML() {
    //     return ['br']
    //   },
    // }),
    Placeholder.configure({
      placeholder: props.placeholder,
    }),
    History,
    ProcessHanzi,
    // CustomKeymap,
  ],
  content: modelToJson(content.value),
  onUpdate({ editor }) {
    content.value = jsonToModel(editor.getJSON())
  },
  onFocus() {
    emit('focus')
  },
  onBlur() {
    emit('blur')
  },
})
</script>

<style>
/* CardPad 对所有元素加了 transition，后期需要优化掉 */
.hanzi-editor * {
  transition: none !important;
}

.hanzi-editor p {
  font-family: sans-serif;
  font-size: 20px;
  line-height: 2;
  word-break: break-all;
}
.hanzi-editor span[data-hanzi] {
  margin: 0 2px;
}
.hanzi-editor span[data-hanzi] rt {
  font-size: 14px;
}
.hanzi-editor p[data-placeholder] {
  font-size: 14px;
  line-height: 40px;
}
</style>
